export const pad = (n) => String(n).padStart(2, "0");

export const formatLocalDate = (date) => {
    const localDate = new Date(date);
    return `${localDate.getFullYear()}-${pad(localDate.getMonth() + 1)}-${pad(
        localDate.getDate()
    )}`;
};

export const getMonthKey = (date) =>
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}`;

export const getCurrentMonthKey = (anchorDate = new Date()) =>
    getMonthKey(new Date(anchorDate.getFullYear(), anchorDate.getMonth(), 1));

export const getLastCompletedMonthKey = (anchorDate = new Date()) =>
    getMonthKey(new Date(anchorDate.getFullYear(), anchorDate.getMonth() - 1, 1));

export const isMonthKey = (key) => /^\d{4}-\d{2}$/.test(String(key || ""));

export const isSelectableMonthKey = (
    key,
    { anchorDate = new Date(), maxMonthKey = getLastCompletedMonthKey(anchorDate) } = {}
) => isMonthKey(key) && compareMonthKeys(key, maxMonthKey) <= 0;

export const getCurrentMonthRange = () => {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = today.getMonth() + 1;

    return {
        start: `${yyyy}-${pad(mm)}-01`,
        end: formatLocalDate(today),
    };
};

export const getDefaultRangeMonthKeys = (anchorDate = new Date()) => {
    const mA = new Date(anchorDate.getFullYear(), anchorDate.getMonth() - 2, 1);
    const mB = new Date(anchorDate.getFullYear(), anchorDate.getMonth() - 1, 1);

    return {
        startKey: `${mA.getFullYear()}-${pad(mA.getMonth() + 1)}`,
        endKey: `${mB.getFullYear()}-${pad(mB.getMonth() + 1)}`,
    };
};

export const getValidatedMonthRange = ({
    startKey,
    endKey,
    anchorDate = new Date(),
    allowSameMonth = true,
} = {}) => {
    const fallback = getDefaultRangeMonthKeys(anchorDate);
    const maxMonthKey = getLastCompletedMonthKey(anchorDate);

    const normalizedStart = isSelectableMonthKey(startKey, {
        anchorDate,
        maxMonthKey,
    })
        ? startKey
        : fallback.startKey;
    const normalizedEnd = isSelectableMonthKey(endKey, {
        anchorDate,
        maxMonthKey,
    })
        ? endKey
        : fallback.endKey;

    if (compareMonthKeys(normalizedStart, normalizedEnd) > 0) {
        return { ...fallback, maxMonthKey };
    }

    if (!allowSameMonth && normalizedStart === normalizedEnd) {
        return { ...fallback, maxMonthKey };
    }

    return {
        startKey: normalizedStart,
        endKey: normalizedEnd,
        maxMonthKey,
    };
};

export const monthKeyToRange = (key) => {
    if (!key) return null;
    const [y, m] = key.split("-").map(Number);
    const last = new Date(y, m, 0).getDate();
    return {
        start: `${y}-${pad(m)}-01`,
        end: `${y}-${pad(m)}-${pad(last)}`,
    };
};

export const monthKeyToLabel = (key) => {
    if (!key) return "";
    const [y, m] = key.split("-").map(Number);
    const d = new Date(y, m - 1, 1);
    return d.toLocaleDateString("en-US", { month: "long", year: "numeric" });
};

export const monthKeyToShortLabel = (key) => {
    if (!key) return "";
    const [y, m] = key.split("-").map(Number);
    const d = new Date(y, m - 1, 1);
    return d.toLocaleDateString("en-US", { month: "short", year: "numeric" });
};

export const monthKeyToDate = (key) => {
    if (!key) return null;
    const [y, m] = key.split("-").map(Number);
    return new Date(y, m - 1, 1);
};

export const compareMonthKeys = (left, right) => {
    if (!left || !right) return 0;
    return left.localeCompare(right);
};

export const getRecentMonthOptions = ({
    count = 12,
    anchorDate = new Date(),
    includeCurrentMonth = false,
} = {}) => {
    const optionAnchor = new Date(anchorDate.getFullYear(), anchorDate.getMonth(), 1);
    const anchorOffset = includeCurrentMonth ? 0 : -1;

    return Array.from({ length: count }, (_, index) => {
        const optionDate = new Date(
            optionAnchor.getFullYear(),
            optionAnchor.getMonth() + anchorOffset - index,
            1
        );
        const key = getMonthKey(optionDate);

        return {
            key,
            label: monthKeyToLabel(key),
        };
    });
};
